import { useState } from "react";
import {
    addMonths,
    eachDayOfInterval,
    endOfMonth,
    endOfWeek,
    format,
    isSameMonth,
    isToday,
    startOfMonth,
    startOfWeek,
} from "date-fns";
import { ChevronLeft, ChevronRight } from "lucide-react";

const STATUS_COLORS = {
    new: "bg-monster-yellow text-monster-navy",
    confirmed: "bg-monster-blue text-white",
    completed: "bg-monster-green text-monster-navy",
    cancelled: "bg-monster-red text-white line-through opacity-60",
};

export const AdminCalendar = ({ bookings }) => {
    const [month, setMonth] = useState(new Date());
    const days = eachDayOfInterval({
        start: startOfWeek(startOfMonth(month)),
        end: endOfWeek(endOfMonth(month)),
    });
    const byDate = {};
    (bookings || []).forEach((b) => {
        (byDate[b.event_date] = byDate[b.event_date] || []).push(b);
    });

    return (
        <div data-testid="admin-calendar" className="overflow-hidden rounded-3xl border-2 border-monster-navy bg-white shadow-sticker">
            <div className="flex items-center justify-between border-b-2 border-monster-navy px-6 py-4">
                <h2 className="font-display text-lg font-semibold text-monster-navy">{format(month, "MMMM yyyy")}</h2>
                <div className="flex gap-2">
                    <button
                        data-testid="calendar-prev-month"
                        onClick={() => setMonth((m) => addMonths(m, -1))}
                        className="rounded-full border-2 border-monster-navy bg-white p-2 shadow-sticker-sm transition-transform hover:-translate-y-0.5"
                        aria-label="Previous month"
                    >
                        <ChevronLeft className="h-4 w-4" />
                    </button>
                    <button
                        data-testid="calendar-next-month"
                        onClick={() => setMonth((m) => addMonths(m, 1))}
                        className="rounded-full border-2 border-monster-navy bg-white p-2 shadow-sticker-sm transition-transform hover:-translate-y-0.5"
                        aria-label="Next month"
                    >
                        <ChevronRight className="h-4 w-4" />
                    </button>
                </div>
            </div>
            <div className="grid grid-cols-7 border-b-2 border-monster-navy bg-monster-sky">
                {["Sun", "Mon", "Tue", "Wed", "Thu", "Fri", "Sat"].map((d) => (
                    <div key={d} className="px-2 py-2 text-center font-display text-xs font-semibold uppercase tracking-wider text-monster-navy/60">
                        {d}
                    </div>
                ))}
            </div>
            <div className="grid grid-cols-7">
                {days.map((day) => {
                    const key = format(day, "yyyy-MM-dd");
                    const dayBookings = byDate[key] || [];
                    const inMonth = isSameMonth(day, month);
                    return (
                        <div
                            key={key}
                            data-testid={`calendar-day-${key}`}
                            className={`min-h-[90px] border-b border-r border-monster-navy/10 p-1.5 ${inMonth ? "bg-white" : "bg-monster-cream/60"} ${
                                isToday(day) ? "ring-2 ring-inset ring-monster-yellow" : ""
                            }`}
                        >
                            <div className={`text-xs font-bold ${inMonth ? "text-monster-navy" : "text-monster-navy/30"}`}>{format(day, "d")}</div>
                            <div className="mt-1 space-y-1">
                                {dayBookings.map((b) => (
                                    <div
                                        key={b.id}
                                        data-testid={`calendar-booking-${b.id}`}
                                        title={`${b.inflatable_name} — ${b.name} (${b.status})`}
                                        className={`truncate rounded-md border border-monster-navy px-1.5 py-0.5 text-[10px] font-bold ${STATUS_COLORS[b.status] || "bg-white"}`}
                                    >
                                        {b.inflatable_name}
                                    </div>
                                ))}
                            </div>
                        </div>
                    );
                })}
            </div>
            <div className="flex flex-wrap gap-4 px-6 py-3 text-xs font-bold text-monster-navy/60">
                <span className="flex items-center gap-1.5"><span className="h-3 w-3 rounded-full border border-monster-navy bg-monster-yellow" /> New</span>
                <span className="flex items-center gap-1.5"><span className="h-3 w-3 rounded-full border border-monster-navy bg-monster-blue" /> Confirmed</span>
                <span className="flex items-center gap-1.5"><span className="h-3 w-3 rounded-full border border-monster-navy bg-monster-green" /> Completed</span>
                <span className="flex items-center gap-1.5"><span className="h-3 w-3 rounded-full border border-monster-navy bg-monster-red" /> Cancelled</span>
            </div>
        </div>
    );
};
